<template>
  <VariableAssignmentMenu
    v-if="request"
    :key="requestKey"
    ref="menu"
    :variableId="variableId"
    :variableName="request.variableName"
    :spaceId="currentSpaceId"
    :saving="saving"
    :pendingUserId="pendingUserId"
    @onAssignUser="onAssignUser"
    @onDeleteRecipient="onDeleteRecipient"
  />
</template>

<script lang="ts">
import { Component, Ref, Vue, Watch } from 'vue-property-decorator'
import { mapState, mapStores } from 'pinia'
import type { Editor } from '@tiptap/core'
import VariableAssignmentMenu from '@/modules/global/components/VariableAssignmentMenu.vue'
import { variableAssignmentBus, type VariableAssignmentRequest } from '@/util/variable-assignment-bus'
import { RootEntityType } from '@/modules/elements/models/Element'
import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import { useSpaceVariableStore } from '@/modules/space/stores/SpaceVariableStore'
import { useUserStore } from '@/modules/user/stores/UserStore'
import { useSnackbarStore } from '@/modules/global/stores/SnackbarStore'
import CreateVariableAssignmentRequest, {
  VariableAssignment
} from '@/modules/space/models/CreateVariableAssignmentRequest'
import { buildVariableAssignmentTransaction } from '@/util/variable-assignment-transform'
import { extractVariablesFromDocument } from '@/util/variable-extractor'
import {
  handleDocumentSignedVariableSchemaError,
  promptUnlockIfVariableSchemaLocked
} from '@/util/variable-schema-lock'
import SpaceVariable from '@/modules/space/models/SpaceVariable'
import User from '@/modules/user/models/User'
import { watchPendingCallouts } from '@/util/variable-assignment-pending'

/**
 * Assign-recipient menu for editors whose host has no handler of its own (message blocks keep theirs). Mirrors
 * MessageElementComponent's assign/delete flow, then refreshes every live editor holding the variable.
 */
@Component({
  components: { VariableAssignmentMenu },
  computed: {
    ...mapState(useSpaceStore, ['currentSpaceId']),
    ...mapState(useUserStore, ['users']),
    ...mapStores(useSpaceVariableStore)
  }
})
export default class VariableAssignmentProvider extends Vue {
  @Ref('menu') menuRef?: VariableAssignmentMenu

  private currentSpaceId!: string
  private users!: User[]
  private spaceVariableStore!: ReturnType<typeof useSpaceVariableStore>
  private selectedVariableId = ''
  private requestKey = 0
  private saving = false
  // '' not undefined: a class field left undefined is not made reactive
  private pendingUserId = ''
  private stopCalloutWatch: (() => void) | null = null

  beforeDestroy() {
    this.stopCalloutWatch?.()
  }

  get request(): VariableAssignmentRequest | null {
    return variableAssignmentBus.request
  }

  get variableId(): string | undefined {
    return this.selectedVariableId || undefined
  }

  private get isTemplateContext(): boolean {
    return this.request?.rootEntityType === RootEntityType.SPACE_PAGE_TEMPLATE
  }

  @Watch('request')
  onRequest(request: VariableAssignmentRequest | null) {
    if (!request) return
    if (!this.currentSpaceId) {
      this.resetSelection()
      return
    }
    const variable = this.findVariable(request.variableId, request.variableName)
    if (this.isTemplateContext && variable && !variable.defaultValue?.trim()) {
      useSnackbarStore().enqueue({
        variant: 'alert',
        leadingIcon: true,
        label: this.$t('variableAssignment.templateContext') as string
      })
      this.resetSelection()
      return
    }
    this.selectedVariableId = request.variableId || variable?.id || ''
    this.requestKey += 1
    this.openMenu()
  }

  private openMenu() {
    // The menu is rendered conditionally, so it exists only after the next tick
    this.$nextTick(() => {
      const element = this.request?.element
      if (this.menuRef && element) (this.menuRef as any).open(element)
    })
  }

  /** The menu no longer hides on every click, so an exit that starts no save closes it (its reopen would toggle it). */
  private closeMenu() {
    this.menuRef?.close()
  }

  private findVariable(variableId?: string, variableName?: string): SpaceVariable | undefined {
    const spaceId = this.currentSpaceId
    return (
      (variableId ? this.spaceVariableStore.getVariableById(spaceId, variableId) : undefined) ||
      (variableName ? this.spaceVariableStore.getVariableByName(spaceId, variableName) : undefined)
    )
  }

  private get isSpacePage(): boolean {
    return this.request?.rootEntityType === RootEntityType.SPACE_PAGE
  }

  async onAssignUser(payload: { userId: string; assignAllUnassigned: boolean } | string) {
    const request = this.request
    if (!request || !this.currentSpaceId || this.saving) return
    // The unlock prompt resumes later: only for the request it was raised for
    const resume = () => (this.request === request ? this.onAssignUser(payload) : undefined)
    if (this.isSpacePage && promptUnlockIfVariableSchemaLocked({ resume })) {
      this.closeMenu()
      return
    }

    const userId = typeof payload === 'string' ? payload : payload.userId
    const assignAllUnassigned = typeof payload === 'string' ? false : payload.assignAllUnassigned
    const variable = this.findVariable(this.selectedVariableId, request.variableName)
    const variableId = this.selectedVariableId || variable?.id
    if (!variableId) {
      this.closeMenu()
      return
    }

    const snackbarStore = useSnackbarStore()
    this.beginSaving(request, userId)
    try {
      if (variable?.assignedToRecipientId) {
        await this.spaceVariableStore.updateAssignment(this.currentSpaceId, variableId, userId)
      } else {
        const assignment = new VariableAssignment()
        assignment.spaceId = this.currentSpaceId
        assignment.variableId = variableId
        assignment.recipientId = userId

        const body = new CreateVariableAssignmentRequest()
        body.assignments = [assignment]
        body.assignAllUnassigned = assignAllUnassigned
        body.recipientId = userId
        if (assignAllUnassigned) {
          body.documentVariableIds = this.bulkAssignIds(variableId, request.editor)
        }
        await this.spaceVariableStore.saveAssignments(this.currentSpaceId, body)
      }
      await this.$nextTick()
      snackbarStore.enqueue({
        variant: 'success',
        leadingIcon: true,
        label: this.$t('variableAssignment.success') as string
      })
      this.safeRefresh(variableId, request)
      this.resetSelection(request)
    } catch (error: any) {
      if (handleDocumentSignedVariableSchemaError(error, this.isSpacePage, { resume })) {
        this.closeMenu()
        return
      }
      console.error('Failed to assign recipient to variable:', error)
      snackbarStore.enqueue({
        variant: 'alert',
        leadingIcon: true,
        label: error?.response?.data?.description || (this.$t('variableAssignment.error') as string)
      })
    } finally {
      this.endSaving()
    }
  }

  async onDeleteRecipient() {
    const request = this.request
    if (!request || !this.currentSpaceId || this.saving) return
    if (this.isSpacePage && promptUnlockIfVariableSchemaLocked({ resume: () => this.openMenu() })) {
      this.closeMenu()
      return
    }

    const variable = this.findVariable(this.selectedVariableId, request.variableName)
    const variableId = this.selectedVariableId || variable?.id
    if (!variableId || !variable?.assignedToRecipientId) {
      this.closeMenu()
      return
    }

    const snackbarStore = useSnackbarStore()
    this.beginSaving(request)
    try {
      await this.spaceVariableStore.deleteAssignment(this.currentSpaceId, variableId, variable.assignedToRecipientId)
      await this.$nextTick()
      snackbarStore.enqueue({
        variant: 'success',
        leadingIcon: true,
        label: this.$t('variableAssignment.deleteSuccess') as string
      })
      this.safeRefresh(variableId, request)
      this.resetSelection(request)
    } catch (error: any) {
      if (
        handleDocumentSignedVariableSchemaError(error, this.isSpacePage, {
          resume: () => this.openMenu()
        })
      ) {
        this.closeMenu()
        return
      }
      console.error('Failed to delete recipient assignment:', error)
      snackbarStore.enqueue({
        variant: 'alert',
        leadingIcon: true,
        label: error?.response?.data?.description || (this.$t('variableAssignment.deleteError') as string)
      })
    } finally {
      this.endSaving()
    }
  }

  /** Spinner on the menu row and on the callout until the save settles; the API call can take a few seconds. */
  private beginSaving(request: VariableAssignmentRequest, userId?: string) {
    this.saving = true
    this.pendingUserId = userId ?? ''
    this.stopCalloutWatch?.()
    this.stopCalloutWatch = watchPendingCallouts(request.element, request.variableName, request.editor?.view?.dom)
  }

  private endSaving() {
    this.saving = false
    this.pendingUserId = ''
    this.stopCalloutWatch?.()
    this.stopCalloutWatch = null
  }

  /** The clicked variable plus the unassigned, default-less variables of the clicked editor's document */
  private bulkAssignIds(clickedVariableId: string, editor?: Editor | null): string[] {
    const ids = new Set<string>([clickedVariableId])
    if (!editor?.state) return Array.from(ids)
    const byName = new Map(
      this.spaceVariableStore.getVariablesBySpaceId(this.currentSpaceId).map(v => [v.variableName.toLowerCase(), v])
    )
    for (const name of extractVariablesFromDocument(editor)) {
      const v = byName.get(name.toLowerCase())
      if (v && !v.assignedToRecipientId && !v.defaultValue?.trim() && !v.recipientValue?.trim()) ids.add(v.id)
    }
    return Array.from(ids)
  }

  /** The save has gone through: a failure while repainting the editors must not read as a failed assignment. */
  private safeRefresh(variableId: string, request: VariableAssignmentRequest) {
    try {
      this.refreshEditors(variableId, request)
    } catch (error) {
      console.error('Failed to refresh editors after a variable assignment change:', error)
    }
  }

  /**
   * Message blocks refresh themselves (they also cover static blocks); every other live editor gets the transaction
   * directly. The tree walk does not descend into a block that refreshes itself, so no editor is updated twice.
   */
  private refreshEditors(variableId: string, request: VariableAssignmentRequest) {
    const scopeId = this.currentSpaceId
    const variable = this.findVariable(variableId, request.variableName)
    if (!scopeId || !variable) return

    const done = new Set<Editor>()
    const update = (editor?: Editor | null) => {
      if (!editor?.state || editor.isDestroyed || done.has(editor)) return
      done.add(editor)
      const tr = buildVariableAssignmentTransaction(
        editor.state,
        variable,
        variableId,
        request.variableName,
        this.users
      )
      if (tr) editor.view.dispatch(tr)
    }

    const walk = (component: Vue): void => {
      const self = component as any
      if (typeof self.updateVariablePlaceholdersAfterAssignment === 'function') {
        self.updateVariablePlaceholdersAfterAssignment(variableId, request.variableName)
        return
      }
      // By declared name: minified builds rename the class and strip the dev-only file option
      if (self.$options?.name === 'TextEditor') {
        update(self.editor)
      }
      component.$children?.forEach(walk)
    }
    let root: Vue = this.$root
    while (root.$parent) root = root.$parent
    walk(root)
    // A pooled editor lives in the pool's own host, outside this tree
    update(request.editor)
  }

  /** `finished` is the request a save belonged to: when another one was opened meanwhile, it stays. */
  private resetSelection(finished?: VariableAssignmentRequest) {
    if (finished && variableAssignmentBus.request !== finished) return
    this.selectedVariableId = ''
    variableAssignmentBus.request = null
  }
}
</script>
